import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Button,
  Form,
} from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import PageLayout from "../../../layouts/PageLayout";

// Local date (toISOString is UTC, which is yesterday before 5am in PKT)
const today = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const emptyRow = () => ({
  amount: "",
  loan_date: today(),
  time_period: "",
  purpose: "",
  evidence: null,
});

export default function ActGroupLoans() {
  const { id } = useParams();

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${AuthToken}`;

  // ============================================================
  // STATES
  // ============================================================

  const [loading, setLoading] = useState(true);

  const [group, setGroup] = useState(null);

  const [loans, setLoans] = useState([]);

  const [purposes, setPurposes] = useState({});

  const [summary, setSummary] = useState({
    total_savings: 0,
    total_loans: 0,
    loans_count: 0,
    balance: 0,
  });

  // Loan form of every member row: { [memberId]: {...} }
  const [rows, setRows] = useState({});

  const [savingMemberId, setSavingMemberId] = useState(null);

  // Resets file inputs after a loan is saved
  const [fileInputKey, setFileInputKey] = useState(0);

  // ============================================================
  // LOAD
  // ============================================================

  useEffect(() => {
    fetchLoans();
  }, [id]);

  const fetchLoans = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/act-groups/${id}/loans`
      );

      const data = response.data.data;

      setGroup(data.group);
      setLoans(data.loans || []);
      setPurposes(data.purposes || {});
      setSummary(data.summary);
    } catch (error) {
      console.error("Error loading ACT Group loans:", error);

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to load ACT Group loans.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // HELPERS
  // ============================================================

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-GB");
  };

  const formatAmount = (amount) => {
    if (
      amount === null ||
      amount === undefined ||
      amount === ""
    ) {
      return "0";
    }

    return Number(amount).toLocaleString("en-PK");
  };

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://")
    ) {
      return image;
    }

    const baseUrl = process.env.REACT_APP_IMG_URL || "";

    return `${baseUrl}${image}`;
  };

  const getRow = (memberId) => rows[memberId] || emptyRow();

  const updateRow = (memberId, field, value) => {
    setRows((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || emptyRow()),
        [field]: value,
      },
    }));
  };

  const memberLoanTotal = (memberId) =>
    loans
      // member_id of a just-created loan comes back as a string
      .filter((loan) => Number(loan.member_id) === Number(memberId))
      .reduce((sum, loan) => sum + Number(loan.amount || 0), 0);

  // ============================================================
  // GIVE LOAN
  // ============================================================

  const handleGiveLoan = async (member) => {
    const row = getRow(member.id);

    if (!row.amount || Number(row.amount) <= 0) {
      Swal.fire("Required", "Please enter loan amount.", "warning");
      return;
    }

    if (!row.loan_date) {
      Swal.fire("Required", "Please select loan date.", "warning");
      return;
    }

    if (!row.purpose) {
      Swal.fire("Required", "Please select loan purpose.", "warning");
      return;
    }

    if (Number(row.amount) > Number(summary.balance)) {
      const result = await Swal.fire({
        title: "Amount exceeds balance",
        text: `Available balance is PKR ${formatAmount(
          summary.balance
        )}. Do you still want to give this loan?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#077834",
        confirmButtonText: "Yes, give loan",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    const formData = new FormData();

    formData.append("group_id", group.id);
    formData.append("member_id", member.id);
    formData.append("amount", row.amount);
    formData.append("loan_date", row.loan_date);
    formData.append("time_period", row.time_period || "");
    formData.append("purpose", row.purpose);

    if (row.evidence) {
      formData.append("evidence", row.evidence);
    }

    try {
      setSavingMemberId(member.id);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/act-group-loans`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setLoans((prev) => [response.data.data, ...prev]);
      setSummary(response.data.summary);

      setRows((prev) => {
        const updated = { ...prev };
        delete updated[member.id];
        return updated;
      });

      setFileInputKey((key) => key + 1);

      Swal.fire({
        title: "Success!",
        text: `Loan given to ${member.name}.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      const errors = error.response?.data?.errors;

      Swal.fire({
        title: "Error!",
        text: errors
          ? Object.values(errors).flat().join(" ")
          : error.response?.data?.message ||
            "Failed to save loan.",
        icon: "error",
      });
    } finally {
      setSavingMemberId(null);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <PageLayout style={{ height: "100%" }}>
        <div className="text-center py-5">
          <Spinner animation="border" variant="success" />

          <p className="mt-3">Loading Loans...</p>
        </div>
      </PageLayout>
    );
  }

  if (!group) {
    return (
      <PageLayout style={{ height: "100%" }}>
        <div className="text-center py-5">
          <h4>ACT Group Not Found</h4>

          <Link to="/list-act-groups">
            <Button variant="secondary" className="mt-3">
              Back to ACT Groups
            </Button>
          </Link>
        </div>
      </PageLayout>
    );
  }

  const members = group.members || [];

  const summaryCards = [
    {
      title: "Total Savings",
      icon: "savings",
      value: `PKR ${formatAmount(summary.total_savings)}`,
    },
    {
      title: "Total Loans Given",
      icon: "payments",
      value: `PKR ${formatAmount(summary.total_loans)}`,
    },
    {
      title: "Available Balance",
      icon: "account_balance_wallet",
      value: `PKR ${formatAmount(summary.balance)}`,
      danger: Number(summary.balance) < 0,
    },
    {
      title: "Number of Loans",
      icon: "receipt_long",
      value: formatAmount(summary.loans_count),
    },
  ];

  // ============================================================
  // VIEW
  // ============================================================

  return (
    <PageLayout style={{ height: "100%" }}>
      {/* ======================================================
          BREADCRUMB
      ====================================================== */}

      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                <i className="material-icons">payments</i>
                Give Loan — {group.group_name}
                {group.group_code ? ` (${group.group_code})` : ""}
              </h3>

              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link
                    to="/"
                    className="mc-breadcrumb-link"
                    style={{ color: "#077834" }}
                  >
                    Home
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link
                    to="/list-act-groups"
                    className="mc-breadcrumb-link"
                    style={{ color: "#077834" }}
                  >
                    ACT Groups
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link
                    to={`/act-groups/view/${group.id}`}
                    className="mc-breadcrumb-link"
                    style={{ color: "#077834" }}
                  >
                    View
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">Loans</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      {/* ======================================================
          SUMMARY
      ====================================================== */}

      <Row className="mb-2">
        {summaryCards.map((card) => (
          <Col xl={3} lg={6} md={6} sm={12} key={card.title}>
            <div
              className="mc-ecommerce-card"
              style={{
                background: card.danger
                  ? "linear-gradient(to right, rgba(0,0,0,0.85), #b02a37)"
                  : "linear-gradient(to right, rgba(0,0,0,0.85), #077834)",
              }}
            >
              <div className="mc-ecommerce-card-head">
                <h4 className="mc-ecommerce-card-meta">
                  <span>{card.title}</span>
                  <span>{card.value}</span>
                </h4>

                <i className="mc-ecommerce-card-icon material-icons text-light">
                  {card.icon}
                </i>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ======================================================
          MEMBERS — GIVE LOAN
      ====================================================== */}

      <Row>
        <Col xl={12}>
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Group Members</h5>

                <Badge bg="success">{members.length} Members</Badge>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="table-responsive">
                <Table bordered hover className="mb-0 align-middle">
                  <thead
                    style={{
                      backgroundColor: "#077834",
                      color: "#fff",
                    }}
                  >
                    <tr>
                      <th>#</th>
                      <th>Member</th>
                      <th>Loans Taken</th>
                      <th style={{ minWidth: "130px" }}>Amount *</th>
                      <th style={{ minWidth: "150px" }}>Date *</th>
                      <th style={{ minWidth: "130px" }}>Time Period</th>
                      <th style={{ minWidth: "160px" }}>Purpose *</th>
                      <th style={{ minWidth: "200px" }}>Evidence</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {members.length > 0 ? (
                      members.map((member, index) => {
                        const row = getRow(member.id);
                        const isSaving = savingMemberId === member.id;
                        const disabled = !member.status || isSaving;

                        return (
                          <tr key={member.id}>
                            <td>{index + 1}</td>

                            <td>
                              <strong>{member.name || "-"}</strong>

                              <div className="text-muted small">
                                {member.cnic || "-"}
                              </div>

                              {!member.status && (
                                <Badge bg="danger">Inactive</Badge>
                              )}
                            </td>

                            <td>
                              PKR {formatAmount(memberLoanTotal(member.id))}
                            </td>

                            <td>
                              <Form.Control
                                type="number"
                                min="1"
                                placeholder="Amount"
                                value={row.amount}
                                disabled={disabled}
                                onChange={(e) =>
                                  updateRow(member.id, "amount", e.target.value)
                                }
                              />
                            </td>

                            <td>
                              <Form.Control
                                type="date"
                                value={row.loan_date}
                                disabled={disabled}
                                onChange={(e) =>
                                  updateRow(member.id, "loan_date", e.target.value)
                                }
                              />
                            </td>

                            <td>
                              <Form.Control
                                type="text"
                                placeholder="e.g. 6 months"
                                value={row.time_period}
                                disabled={disabled}
                                onChange={(e) =>
                                  updateRow(member.id, "time_period", e.target.value)
                                }
                              />
                            </td>

                            <td>
                              <Form.Select
                                value={row.purpose}
                                disabled={disabled}
                                onChange={(e) =>
                                  updateRow(member.id, "purpose", e.target.value)
                                }
                              >
                                <option value="">Select Purpose</option>

                                {Object.entries(purposes).map(([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>

                            <td>
                              <Form.Control
                                key={`${member.id}-${fileInputKey}`}
                                type="file"
                                accept="image/*"
                                disabled={disabled}
                                onChange={(e) =>
                                  updateRow(
                                    member.id,
                                    "evidence",
                                    e.target.files?.[0] || null
                                  )
                                }
                              />
                            </td>

                            <td>
                              <Button
                                variant="success"
                                size="sm"
                                disabled={disabled}
                                onClick={() => handleGiveLoan(member)}
                                style={{ whiteSpace: "nowrap" }}
                              >
                                {isSaving ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  "Give Loan"
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          No members found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ======================================================
          LOAN HISTORY
      ====================================================== */}

      <Row>
        <Col xl={12}>
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Loans Given</h5>

                <Badge bg="success">{loans.length} Loans</Badge>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="table-responsive">
                <Table bordered hover className="mb-0 align-middle">
                  <thead
                    style={{
                      backgroundColor: "#077834",
                      color: "#fff",
                    }}
                  >
                    <tr>
                      <th>#</th>
                      <th>Member</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Time Period</th>
                      <th>Purpose</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loans.length > 0 ? (
                      loans.map((loan, index) => (
                        <tr key={loan.id}>
                          <td>{index + 1}</td>

                          <td>
                            <strong>{loan.member?.name || "-"}</strong>
                          </td>

                          <td>PKR {formatAmount(loan.amount)}</td>

                          <td>{formatDate(loan.loan_date)}</td>

                          <td>{loan.time_period || "-"}</td>

                          <td>
                            <Badge bg="primary">
                              {loan.purpose_text || loan.purpose}
                            </Badge>
                          </td>

                          <td>
                            {loan.evidence ? (
                              <a
                                href={getImageUrl(loan.evidence)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={getImageUrl(loan.evidence)}
                                  alt="Evidence"
                                  style={{
                                    width: "80px",
                                    height: "60px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    border: "1px solid #ddd",
                                  }}
                                />
                              </a>
                            ) : (
                              <span className="text-muted">No evidence</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No loans given yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col xl={12}>
          <Link to={`/act-groups/view/${group.id}`}>
            <Button variant="secondary">Back to Group</Button>
          </Link>
        </Col>
      </Row>
    </PageLayout>
  );
}
